import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useLocation } from 'react-router-dom';

import { MobileInput } from '../../components/elements';

const Container = styled.section`
  &&& {
    height: 100vh;
    .title {
      font-size: 3rem;
      letter-spacing: 9px;
    }
  }
`;

const FooterTextWrapper = styled.div`
  padding: 1rem 1rem;
  background: #f2f2f2;
`;

const Heading = styled.h2`
  font-size: 20px;
  font-weight: 500;
  color: #1e1e1e;
  margin-bottom: 0.5rem;
  text-align: start;
`;

const Subtext = styled.p`
  font-size: 14px;
  color: #888;
  margin-bottom: 2rem;
  line-height: 1.6;
  text-align: start;
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #1e1e1e;
  margin-bottom: 0.75rem;
`;

const OtpContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const OtpBox = styled.input`
  width: 100%;
  aspect-ratio: 1;
  border: 1px solid
    ${({ filled, haserr }) => {
      if (haserr) return '#f44336';
      if (filled) return '#2196f3';
      return '#e0e0e0';
    }};
  border-radius: 6px;
  text-align: center;
  font-size: 18px;
  font-weight: 500;
  color: #1e1e1e;
  background: #fafafa;
  outline: none;
  caret-color: transparent;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }
`;

const ErrorMsg = styled.p`
  color: #f44336;
  font-size: 13px;
  margin-top: 0.5rem;
  min-height: 18px;
`;

const BtnPrimary = styled.button`
  width: 100%;
  padding: 0.875rem;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background: ${({ success }) => (success ? '#4caf50' : '#2196f3')};
  color: #fff;
  transition: background 0.2s, opacity 0.15s;

  &:hover:not(:disabled) {
    background: ${({ success }) => (success ? '#43a047' : '#1976d2')};
  }

  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
  }
`;

const BtnSecondary = styled.button`
  width: 100%;
  padding: 0.875rem;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background: #f5f5f5;
  color: #1e1e1e;
  margin-top: 1rem;
  transition: background 0.15s;

  &:hover {
    background: #e0e0e0;
  }
`;

const ResendSection = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  font-size: 13px;
  color: #888;
`;

const ResendBtn = styled.button`
  background: none;
  border: none;
  color: #2196f3;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: #1976d2;
  }
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #4caf50;
  border-radius: 50%;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PhoneVerification = ({
  history,
  preLoginFlow = false,
  preLoginBackPress = null,
}) => {
  const location = useLocation();
  const isError = useStoreState(state => state.auth.isError);
  const isLoading = useStoreState(state => state.auth.loading);
  const authTokens = useStoreState(state => state.auth.authTokens);
  const phoneVerificationCallback = useStoreState(
    state => state.auth.phoneVerificationCallback,
  );

  const sendPhoneVerificationCode = useStoreActions(
    state => state.auth.sendPhoneVerificationCode,
  );
  const setPhoneVerificationCallback = useStoreActions(
    state => state.auth.setPhoneVerificationCallback,
  );

  const verifyPhone = useStoreActions(state => state.auth.verifyPhone);
  const clearError = useStoreActions(state => state.auth.clearError);

  const existingPhone = authTokens?.payload?.phone_number || '';

  const [step, setStep] = useState('enter_phone');
  const [phoneNumber, setPhoneNumber] = useState(existingPhone);
  const [phoneError, setPhoneError] = useState('');
  const [otpDigits, setOtpDigits] = useState(Array(6).fill(''));
  const [countdown, setCountdown] = useState(300);
  const [timerKey, setTimerKey] = useState(0);
  const boxRefs = useRef([]);

  useEffect(() => {
    clearError();
  }, []);

  const handleSkip = () => {
    if (preLoginFlow && preLoginBackPress) {
      phoneVerificationCallback();
      preLoginBackPress();
    } else {
      const params = new URLSearchParams(location.search);
      const fallback = params.get('fallback');
      history.push(fallback === 'homepage' ? '/' : '/');
    }
  };

  useEffect(() => {
    if (step !== 'enter_otp') return () => {};
    setCountdown(300);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step, timerKey]);

  useEffect(() => {
    if (step === 'enter_otp' && boxRefs.current[0]) {
      setTimeout(() => boxRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  const handleVerify = code => {
    verifyPhone({
      phoneNumber,
      code: code || otpDigits.join(''),
      history,
      onSuccess: () => setStep('success'),
    });
  };

  const handleSendCode = async () => {
    setPhoneError('');
    if (!phoneNumber.trim()) {
      setPhoneError('Please enter a phone number');
      return;
    }
    if (
      !/^\+?[\d\s().-]+$/.test(phoneNumber) ||
      phoneNumber.replace(/\D/g, '').length < 10
    ) {
      setPhoneError('Please enter a valid phone number');
      return;
    }
    clearError();
    const success = await sendPhoneVerificationCode({ phoneNumber });
    if (success) {
      setStep('enter_otp');
    }
  };

  const handleBoxKeyDown = (e, idx) => {
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      const newDigits = [...otpDigits];
      newDigits[idx] = e.key;
      setOtpDigits(newDigits);
      if (idx < 5) boxRefs.current[idx + 1].focus();
      return;
    }
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (otpDigits[idx]) {
        const newDigits = [...otpDigits];
        newDigits[idx] = '';
        setOtpDigits(newDigits);
      } else if (idx > 0) {
        const newDigits = [...otpDigits];
        newDigits[idx - 1] = '';
        setOtpDigits(newDigits);
        boxRefs.current[idx - 1].focus();
      }
      return;
    }
    if (e.key === 'ArrowLeft' && idx > 0) {
      boxRefs.current[idx - 1].focus();
      return;
    }
    if (e.key === 'ArrowRight' && idx < 5) {
      boxRefs.current[idx + 1].focus();
      return;
    }
    if (e.key === 'Enter') {
      const code = otpDigits.join('');
      if (code.length === 6 && !isLoading) handleVerify(code);
    }
  };

  const handleBoxPaste = e => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData)
      .getData('text')
      .replace(/\D/g, '');
    const newDigits = Array(6).fill('');
    pasted
      .split('')
      .slice(0, 6)
      .forEach((c, j) => {
        newDigits[j] = c;
      });
    setOtpDigits(newDigits);
    boxRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleResend = async () => {
    setOtpDigits(Array(6).fill(''));
    setTimerKey(k => k + 1);
    clearError();
    await sendPhoneVerificationCode({ phoneNumber });
    setTimeout(() => boxRefs.current[0]?.focus(), 100);
  };

  const formatCountdown = secs =>
    `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  const otp = otpDigits.join('');
  const otpAllFilled = otp.length === 6 && !otpDigits.includes('');
  const otpHasError = isError?.error && step === 'enter_otp';

  return (
    <Container className="hero is-medium">
      <div className="hero-body">
        <div className="container has-text-centered">
          <h1 className="title is-uppercase">Kravein</h1>
          <div className="columns is-centered">
            <div className="column is-4">
              {step === 'enter_phone' && (
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    handleSendCode();
                  }}
                >
                  <Heading>Setup your number for OTP</Heading>
                  <Subtext>
                    Enter your phone number to receive verification codes. You
                    can update this anytime.
                  </Subtext>

                  <FormGroup>
                    <MobileInput
                      label="Phone number"
                      value={phoneNumber}
                      onChange={value => {
                        setPhoneNumber(value);
                        setPhoneError('');
                      }}
                    />
                    {phoneError && <ErrorMsg>{phoneError}</ErrorMsg>}
                  </FormGroup>

                  {isError?.error && <ErrorMsg>{isError.message}</ErrorMsg>}

                  <BtnPrimary
                    type="submit"
                    disabled={
                      (!existingPhone && !phoneNumber.trim()) || isLoading
                    }
                  >
                    Send OTP
                  </BtnPrimary>
                  {preLoginFlow && (
                    <BtnSecondary
                      type="button"
                      onClick={() => {
                        if (preLoginBackPress) {
                          preLoginBackPress();
                          setPhoneVerificationCallback(null);
                        }

                        clearError();
                      }}
                    >
                      Back
                    </BtnSecondary>
                  )}
                </form>
              )}

              {step === 'enter_otp' && (
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    if (otpAllFilled) handleVerify();
                  }}
                >
                  <Heading>Setup your number for OTP</Heading>
                  <Subtext>
                    We sent a 6-digit code to your number. Enter it below to
                    setup your number for OTP.
                  </Subtext>

                  <FormGroup>
                    <FormLabel>One-time password</FormLabel>
                    <OtpContainer>
                      {['o0', 'o1', 'o2', 'o3', 'o4', 'o5'].map((k, i) => (
                        <OtpBox
                          key={k}
                          ref={el => {
                            boxRefs.current[i] = el;
                          }}
                          maxLength={1}
                          inputMode="numeric"
                          pattern="[0-9]"
                          value={otpDigits[i]}
                          filled={otpDigits[i] ? 1 : 0}
                          haserr={otpHasError ? 1 : 0}
                          autoComplete={i === 0 ? 'one-time-code' : undefined}
                          onChange={() => {}}
                          onKeyDown={e => handleBoxKeyDown(e, i)}
                          onPaste={handleBoxPaste}
                          onFocus={e => e.target.select()}
                        />
                      ))}
                    </OtpContainer>
                    {otpHasError && <ErrorMsg>{isError.message}</ErrorMsg>}
                  </FormGroup>

                  <BtnPrimary
                    type="submit"
                    disabled={!otpAllFilled || isLoading}
                  >
                    Verify phone number
                  </BtnPrimary>

                  <ResendSection style={{ marginTop: '1rem' }}>
                    {countdown > 0 ? (
                      <span>
                        Resend code in{' '}
                        <strong>{formatCountdown(countdown)}</strong>
                      </span>
                    ) : (
                      <ResendBtn
                        type="button"
                        disabled={isLoading}
                        onClick={handleResend}
                      >
                        Resend code
                      </ResendBtn>
                    )}
                  </ResendSection>

                  <BtnSecondary
                    type="button"
                    onClick={() => {
                      setStep('enter_phone');
                      setOtpDigits(Array(6).fill(''));
                      clearError();
                    }}
                  >
                    Back
                  </BtnSecondary>
                </form>
              )}

              {step === 'success' && (
                <div style={{ textAlign: 'center' }}>
                  <SuccessIcon>
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </SuccessIcon>
                  <Heading style={{ textAlign: 'center' }}>
                    Setup complete
                  </Heading>
                  <Subtext style={{ textAlign: 'center' }}>
                    Your phone number has been verified and set up for OTP.
                  </Subtext>
                  <BtnPrimary success type="button" onClick={handleSkip}>
                    Continue
                  </BtnPrimary>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="hero-foot">
        <FooterTextWrapper className="is-flex">
          <p className="is-size-4">
            @{new Date().getFullYear()} KraveIn All right reserved
          </p>
          <p className="is-size-4">
            <a>Term of use</a> &amp; <a>Privacy policy</a>
          </p>
        </FooterTextWrapper>
      </div>
    </Container>
  );
};

export default PhoneVerification;
